"use client";

import { backgroundGet, foregroundGet } from "@/api/base/private/backgroundClient";

const ENDPOINT = "/assignment-attempts";
const LEGACY_ENDPOINT = (assignmentId: number) => `/assignment/attempts/${assignmentId}.json`;

const MISSING_STATUSES = new Set([404, 405]);
const IGNORED_FILTER_ROWS = 5;

export const ATTEMPT_LIST_FIELDS =
  "id,assignment_id,student_id,status,points,credibility,spending_time,created_at";
export const ATTEMPT_OWNER_FIELDS = "id,assignment_id,student_id,status";
export const ATTEMPT_TARGET_FIELDS =
  "id,assignment_id,student_id,status,assignment:id,assignment:name,assignment:status";

export interface AttemptAssignment {
  id?: number | string | null;
  name?: string | null;
  status?: string | null;
}

export interface AttemptRow {
  id?: number | string | null;
  assignment?: AttemptAssignment | null;
  assignment_id?: number | string | null;
  student_id?: number | string | null;
  status?: string | null;
  points?: number | string | null;
  credibility?: number | string | null;
  spending_time?: number | string | null;
  created_at?: string | null;
}

export type AttemptSearch =
  | { status: "found"; attempt: AttemptRow }
  | { status: "absent" }
  | { status: "unsupported" };

let endpointMissing = false;

const unwrapList = (payload: unknown): AttemptRow[] => {
  const entities = (payload as { entities?: unknown } | null)?.entities;
  if (Array.isArray(entities)) return entities as AttemptRow[];

  const data = (entities as { data?: unknown } | null)?.data;
  if (Array.isArray(data)) return data as AttemptRow[];

  return [];
};

const responseStatus = (error: unknown): number | null => {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === "number" ? status : null;
};

const endpointUnusable = (error: unknown): boolean => {
  const status = responseStatus(error);

  if (status == null) return false;

  return MISSING_STATUSES.has(status) || status >= 500;
};

async function ask(
  params: Record<string, unknown>,
  fields: string,
  paced: boolean
): Promise<AttemptRow[] | null> {
  if (endpointMissing) return null;

  const get = paced ? backgroundGet : foregroundGet;

  try {
    const { data } = await get(ENDPOINT, {
      params,
      headers: { "X-Requested-Fields": fields },
    });

    return unwrapList(data);
  } catch (error) {
    if (endpointUnusable(error)) endpointMissing = true;
    return null;
  }
}

async function askLegacy(
  assignmentId: number,
  studentId: number | null,
  fields: string,
  paced: boolean
): Promise<AttemptRow[]> {
  const get = paced ? backgroundGet : foregroundGet;

  const { data } = await get(LEGACY_ENDPOINT(assignmentId), {
    params: studentId != null ? { student_id: studentId } : {},
    headers: { "X-Requested-Fields": fields },
  });

  return unwrapList(data);
}

export async function fetchStudentAttempts(
  assignmentId: number,
  studentId: number,
  options: { fields?: string; paced?: boolean } = {}
): Promise<AttemptRow[]> {
  const { fields = ATTEMPT_LIST_FIELDS, paced = true } = options;

  const rows = await ask(
    { assignment_id: assignmentId, student_id: studentId, orderBy: "asc" },
    fields,
    paced
  );

  if (rows != null) return rows;

  return askLegacy(assignmentId, studentId, fields, paced);
}

export async function findAttemptById(
  attemptId: number,
  options: { fields?: string; paced?: boolean } = {}
): Promise<AttemptSearch> {
  const { fields = ATTEMPT_TARGET_FIELDS, paced = false } = options;

  if (endpointMissing) return { status: "unsupported" };

  const rows = await ask({ id: attemptId }, fields, paced);

  if (rows == null) return { status: "unsupported" };

  if (rows.length > IGNORED_FILTER_ROWS) {
    endpointMissing = true;
    return { status: "unsupported" };
  }

  const attempt = rows.find((row) => Number(row.id) === attemptId);

  return attempt ? { status: "found", attempt } : { status: "absent" };
}
